import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useEnqueueSnackbar } from 'hooks/useEnqueueSnackbar';
import type { UseModalResult } from 'hooks/useModal';
import { useDeleteFlow } from '../api';
import { ConfirmDialog } from 'components/common';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router';

interface FlowCommonProps {
  id: number;
  name: string;
}

interface DeleteFlowItemProps
  extends FlowCommonProps,
    Pick<UseModalResult, 'open' | 'close' | 'isOpen'> {
}

const DeleteFlow: FC<DeleteFlowItemProps> = ({ isOpen, close, id, name }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { onShowAlert } = useEnqueueSnackbar();
  const navigate = useNavigate();

  const deleteFlow = useDeleteFlow({
    config: {
      onSuccess: () => {
        onShowAlert(`"${name}" flow deleted`);
        close();
        navigate(`/cabinet/facebook/flows`, { replace: true });
      },
      onError: () => {
        setErrorMessage('Internal Error');
      },
    },
  });

  const onConfirm = useCallback(() => {
    setErrorMessage(null);
    deleteFlow.mutate({ id });
  }, [deleteFlow, id]);

  return (
    <>
      <ConfirmDialog
        title={`Delete flow ${name}?`}
        text={
          <>
            <Typography variant="body1">
              {name} will be deleted without the possibility of recovery
            </Typography>
            {!!errorMessage && (
              <Typography
                variant="body1"
                sx={(t) => ({ color: t.palette.error.main, pt: 2 })}
              >
                {errorMessage}
              </Typography>
            )}
          </>
        }
        confirmButton="Delete flow"
        cancelButton="Cancel"
        isOpen={isOpen}
        isPending={deleteFlow.isPending}
        close={() => {
          !deleteFlow.isPending ? close() : null;
        }}
        onCancel={() => {
          !deleteFlow.isPending ? close() : null;
        }}
        onConfirm={onConfirm}
      />
    </>
  );
};

export default DeleteFlow;
